import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import { keyframes } from '@mui/material/styles'
import { useQueryClient } from '@tanstack/react-query'

import { useInstrumentDashboard } from '@/features/campaigns/api/instrument-dashboard-queries'
import { formatInstrumentDashboardLastUpdated } from '@/features/campaigns/lib/insight-display'
import { useDashboardLastUpdatedIso } from '@/features/campaigns/lib/use-dashboard-last-updated'
import { queryKeys } from '@/api/query-keys'
import { RiRefreshLine } from '@/shared/icons/remix'
import { useSessionStore } from '@/stores/useSessionStore'

const refreshSpin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

interface Props {
  instrumentId: string
}

export function AppShellInstrumentDashboardStatus({
  instrumentId,
}: Readonly<Props>) {
  const queryClient = useQueryClient()
  const tenantId = useSessionStore((s) => s.user?.tenantId)
  const tenantKey = tenantId ?? '_'
  const { data: summary, isFetching, refetch, dataUpdatedAt } = useInstrumentDashboard(
    instrumentId,
  )
  const lastUpdatedIso = useDashboardLastUpdatedIso(
    summary?.lastUpdatedAt,
    dataUpdatedAt,
    instrumentId,
  )

  const onRefresh = () => {
    void (async () => {
      await refetch()
      const campaignId = summary?.campaignId
      if (!campaignId) return
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.insights.instrument(tenantKey, campaignId, instrumentId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.insights.recommendation(tenantKey, campaignId, instrumentId),
        }),
      ])
    })()
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexShrink: 0,
      }}
    >
      <Typography
        data-testid="instrument-dashboard-last-updated"
        sx={{
          fontFamily: '"Inter", sans-serif',
          fontSize: '14px',
          lineHeight: '20px',
          letterSpacing: '-0.1504px',
          color: '#4A5565',
          whiteSpace: 'nowrap',
        }}
      >
        Last Update{' '}
        {formatInstrumentDashboardLastUpdated(lastUpdatedIso)}
      </Typography>
      <IconButton
        size="small"
        aria-label={isFetching ? 'Refreshing dashboard' : 'Refresh dashboard'}
        aria-busy={isFetching}
        data-testid="instrument-dashboard-refresh"
        disabled={isFetching}
        onClick={onRefresh}
        sx={{
          width: 36,
          height: 36,
          color: '#4A5565',
          '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
        }}
      >
        <Box
          component="span"
          aria-hidden
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            animation: isFetching ? `${refreshSpin} 0.9s linear infinite` : 'none',
          }}
        >
          <RiRefreshLine size={24} />
        </Box>
      </IconButton>
    </Box>
  )
}
